<template>
  <div class="bg-gray-50">
    <div class="tw-container mx-auto px-4 py-8">
      <!-- Dashboard Header -->
      <div class="mb-8 tw-flex items-center justify-between">
        <div>
          <h2 class="text-3xl font-bold">Your Maps</h2>
          <p class="text-gray-600">
            Manage and monitor all your visual maps
          </p>
        </div>
        <ds-button
          label="Create New Map"
          @click="createNewMap"
          class="gap-2"
          icon="plus"
          variant="rounded"
        />
      </div>

      <!-- Stats Overview -->
      <!--      <div class="mb-8 grid gap-4 md:grid-cols-3">
              <div class="bg-white rounded-lg shadow-sm border p-6">
                <div class="pb-3">
                  <p class="text-sm text-gray-600">Total Maps</p>
                  <h3 class="text-3xl font-bold mt-1">{{ totalMaps }}</h3>
                </div>
              </div>
              <div class="bg-white rounded-lg shadow-sm border p-6">
                <div class="pb-3">
                  <p class="text-sm text-gray-600">Total Views</p>
                  <h3 class="text-3xl font-bold mt-1">{{ totalViews.toLocaleString() }}</h3>
                </div>
              </div>
              <div class="bg-white rounded-lg shadow-sm border p-6">
                <div class="pb-3">
                  <p class="text-sm text-gray-600">Total Views This Month</p>
                  <h3 class="text-3xl font-bold mt-1">{{ totalViewsCurrentMonth.toLocaleString() }}</h3>
                </div>
              </div>
            </div>-->

      <!-- Filters -->
      <div class="mb-6 bg-white rounded-lg shadow-sm border p-4">
        <div class="tw-flex flex-col md:flex-row gap-4">
          <!-- Search Bar -->
          <div class="flex-1">
            <div class="relative">
              <DsInput
                icon="search"
                is-simplified
                v-model="searchQuery"
                placeholder="Search maps by name..."
              />
            </div>
          </div>

          <!-- Spotting Area Filter -->
          <div class="w-full md:w-64">
            <select
              v-model="selectedSpottingAreaId"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option :value="null">All Spotting Areas</option>
              <option
                v-for="area in availableSpottingAreas"
                :key="area.id"
                :value="area.id"
              >
                {{ area.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Maps List -->
      <div class="grid gap-4">
        <div
          v-for="map in filteredMaps"
          :key="map.id"
          class="bg-white rounded-lg shadow-sm border transition-all hover:shadow-lg"
        >
          <div class="p-6">
            <div class="tw-flex items-start justify-between">
              <div class="flex-1">
                <div class="mb-2 tw-flex items-center gap-3">
                  <h3 class="text-xl font-semibold">{{ map.name }}</h3>
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getStatusBadgeClass(map.status)"
                  >
                    {{ map.status }}
                  </span>
                </div>
                <div class="mb-4 tw-flex gap-6 text-sm text-gray-600">
                  <span class="tw-flex items-center gap-1">
                    <Icon name="microscope" size="12"/>
                    {{ map?.spottingArea?.name }}
                  </span>
                  <!--                  <span class="tw-flex items-center gap-1">
                                      <Icon name="eye" class="h-4 w-4"/>
                                      {{ map.views.toLocaleString() }} views
                                    </span>-->
                  <span>Updated {{ map.updatedAt }}</span>
                </div>
                <div class="tw-flex gap-2">
                  <ds-button
                    label="Get Embed Code"
                    variant="rounded-minimal"
                    size="sm"
                    icon="code"
                    icon-fill-color="black"
                    @click="getEmbedCode(map)"
                  />
                  <ds-button
                    label="Configure"
                    variant="rounded"
                    size="sm"
                    @click="configureMap(map)"
                  />
                </div>
              </div>

              <DropdownActionsButton
                v-if="dropdownActions && dropdownActions.length > 0 && this.isOwner"
                :actions="getDropdownActionsForMap(map)"
              >
                <template v-slot:button>
                  <div class="bg-white rounded hover:bg-primary-100 hover:cursor-pointer">
                    <DsButton
                      icon="ellipsis-v"
                      icon-fill-color="black"
                      variant="rounded-minimal"
                    />
                  </div>
                </template>
              </DropdownActionsButton>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredMaps.length === 0 && maps.length === 0" class="text-center py-16">
        <div class="w-16 h-16 bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4">
          <Icon name="map-pin" class="w-8 h-8 text-gray-400"/>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No maps yet</h3>
        <p class="text-gray-500 max-w-md mx-auto mb-4">
          Create your first ecosystem map to start visualizing your data
        </p>
        <ds-button
          label="Create Your First Map"
          @click="createNewMap"
          icon="plus"
        />
      </div>

      <!-- No Results State -->
      <div v-if="filteredMaps.length === 0 && maps.length > 0" class="text-center py-16">
        <div class="w-16 h-16 bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4">
          <Icon name="search" class="w-8 h-8 text-gray-400"/>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No maps found</h3>
        <p class="text-gray-500 max-w-md mx-auto mb-4">
          Try adjusting your search or filter criteria
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import * as EcosystemMapsApi from '@/Domain/EcosystemMap/Api/ecosystemMaps'
  import DropdownActionsButton from '@/Domain/Shared/Components/DropdownActionsButton.vue'
  import UserMixin from '@/util/UserMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import DsInput from '@/components/Form/DsInput.vue'

  export default defineComponent({
    name: 'EcosystemMaps',
    mixins: [UserMixin],
    components: {
      DsInput,
      DropdownActionsButton,
      Icon,
      DsButton
    },
    data () {
      return {
        maps: [],
        stats: {
          total_maps: 0,
          total_views: 0,
          total_views_this_month: 0
        },
        loading: true,
        error: null,
        showDeleteModal: false,
        mapToDelete: null,
        deleting: false,
        searchQuery: '',
        selectedSpottingAreaId: null
      }
    },
    computed: {
      totalMaps () {
        return this.stats.total_maps
      },
      totalViews () {
        return this.stats.total_views
      },
      totalViewsCurrentMonth () {
        return this.stats.total_views_this_month
      },
      totalSpottingAreas () {
        return this.stats.total_spotting_areas
      },
      dropdownActions () {
        return [
          (this.isOwner || this.isMember) && {
            label: 'Delete',
            icon: 'trash',
            analyticsId: 'ecosystemMaps.delete',
            variant: 'danger'
          },
        ].filter(Boolean)
      },
      availableSpottingAreas () {
        const areas = []
        const seenIds = new Set()

        this.maps.forEach(map => {
          if (map.spottingAreas && Array.isArray(map.spottingAreas)) {
            map.spottingAreas.forEach(area => {
              if (area && area.id && !seenIds.has(area.id)) {
                seenIds.add(area.id)
                areas.push({
                  id: area.id,
                  name: area.name
                })
              }
            })
          } else if (map.spottingArea && map.spottingArea.id && !seenIds.has(map.spottingArea.id)) {
            seenIds.add(map.spottingArea.id)
            areas.push({
              id: map.spottingArea.id,
              name: map.spottingArea.name
            })
          }
        })

        return areas.sort((a, b) => a.name.localeCompare(b.name))
      },
      filteredMaps () {
        let filtered = this.maps

        // Filter by search query
        if (this.searchQuery && this.searchQuery.trim() !== '') {
          const query = this.searchQuery.toLowerCase().trim()
          filtered = filtered.filter(map => {
            return map.name && map.name.toLowerCase().includes(query)
          })
        }

        // Filter by spotting area
        if (this.selectedSpottingAreaId !== null) {
          filtered = filtered.filter(map => {
            if (map.spottingAreas && Array.isArray(map.spottingAreas)) {
              return map.spottingAreas.some(area => area.id === this.selectedSpottingAreaId)
            } else if (map.spottingArea) {
              return map.spottingArea.id === this.selectedSpottingAreaId
            }
            return false
          })
        }

        return filtered
      }
    },
    async mounted () {
      await this.loadMaps()

      this.$bus.on('ecosystemMapDeleteConfirmation', (modalContext) => {
        this.confirmDeleteMap(modalContext.resource)
      })
    },
    beforeUnmount () {
      this.$bus.off('ecosystemMapDeleteConfirmation', (modalContext) => {
        this.confirmDeleteMap(modalContext.resource)
      })
    },
    methods: {
      getDropdownActionsForMap (map) {
        return [
          (this.isOwner || this.isMember) && {
            onClick: () => {
              this.openConfirmDeleteMapModal(map)
            },
            label: 'Delete',
            icon: 'trash',
            analyticsId: 'ecosystemMaps.delete',
            variant: 'danger'
          },
        ].filter(Boolean)
      },
      openConfirmDeleteMapModal (map) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          body: 'Are you sure you want to delete this ecosystem map, this map will become unavailable if implemented in a website?',
          resource: map,
          modalContextType: 'ecosystem-map'
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },

      async confirmDeleteMap (map) {
        if (!map) return

        this.deleting = true
        try {
          await EcosystemMapsApi.deleteEcosystemMap(map.id)

          // Remove map from the list
          this.maps = this.maps.filter(m => m.id !== map.id)

          // Update stats
          this.stats.total_maps = Math.max(0, this.stats.total_maps - 1)
          this.stats.total_views = Math.max(0, this.stats.total_views - this.mapToDelete.views)
        } catch (error) {
          console.error('Failed to delete map:', error)
          this.deleting = false
        }
      },
      async loadMaps () {
        try {
          this.loading = true
          this.error = null
          const response = await EcosystemMapsApi.fetchEcosystemMaps()
          this.maps = response.maps || []
          this.stats = response.stats || {}
        } catch (error) {
          console.error('Failed to load ecosystem maps:', error)
          this.error = 'Failed to load ecosystem maps'
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: 'Failed to load ecosystem maps'
          })
        } finally {
          this.loading = false
        }
      },

      createNewMap () {
        this.$router.push('/settings/ecosystem-maps/new')
      },

      async getEmbedCode (map) {
        try {
          await navigator.clipboard.writeText(map.embed_code || '')
          this.$store.dispatch('showNotification', {
            type: 'success',
            message: 'Embed code copied to clipboard!'
          })
        } catch (error) {
          console.error('Failed to copy embed code:', error)
          this.$store.dispatch('showNotification', {
            type: 'error',
            message: 'Failed to copy embed code'
          })
        }
      },

      configureMap (map) {
        this.$router.push(`/settings/ecosystem-maps/${map.id}`)
      },

      getStatusBadgeClass (status) {
        switch (status) {
          case 'active':
            return 'bg-green-100 text-green-800'
          case 'inactive':
            return 'bg-gray-100 text-gray-800'
          case 'draft':
            return 'bg-yellow-100 text-yellow-800'
          default:
            return 'bg-gray-100 text-gray-800'
        }
      }
    }
  })
</script>

<style lang="scss" scoped>
  .tw-container {
    max-width: 1200px;
  }
</style>
